<template>
  <div class="sync-history">
    <div class="header mb-4 flex justify-between items-center">
      <h3 class="text-xl font-semibold">Sync History</h3>
      <button 
        @click="$emit('close')"
        class="text-gray-500 hover:text-gray-700"
      >
        <i class="fas fa-times text-xl"></i>
      </button>
    </div>

    <!-- Filters -->
    <div class="filters bg-gray-100 rounded p-4 mb-4">
      <div class="grid grid-cols-4 gap-4">
        <div>
          <label class="block text-sm font-medium mb-1">Status</label>
          <select 
            v-model="filters.status"
            class="form-select w-full"
            @change="fetchLogs"
          >
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium mb-1">From Date</label>
          <input 
            type="date"
            v-model="filters.from_date"
            class="form-input w-full"
            @change="fetchLogs"
          >
        </div>

        <div>
          <label class="block text-sm font-medium mb-1">To Date</label>
          <input 
            type="date"
            v-model="filters.to_date"
            class="form-input w-full"
            @change="fetchLogs"
          >
        </div>

        <div class="flex items-end">
          <button 
            @click="clearFilters"
            class="btn btn-secondary btn-sm"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>

    <!-- Statistics -->
    <div class="statistics grid grid-cols-4 gap-4 mb-6">
      <div class="stat-card bg-white rounded-lg shadow p-4">
        <div class="text-2xl font-bold text-blue-600">{{ stats.total }}</div>
        <div class="text-sm text-gray-600">Total Syncs</div>
      </div>
      
      <div class="stat-card bg-white rounded-lg shadow p-4">
        <div class="text-2xl font-bold text-green-600">{{ stats.success }}</div>
        <div class="text-sm text-gray-600">Successful</div>
      </div>
      
      <div class="stat-card bg-white rounded-lg shadow p-4">
        <div class="text-2xl font-bold text-red-600">{{ stats.failed }}</div>
        <div class="text-sm text-gray-600">Failed</div>
      </div>
      
      <div class="stat-card bg-white rounded-lg shadow p-4">
        <div class="text-2xl font-bold text-cyan-600">{{ stats.success_rate }}%</div>
        <div class="text-sm text-gray-600">Success Rate</div>
      </div>
    </div>

    <!-- Logs Table -->
    <div class="logs-table bg-white rounded-lg shadow overflow-hidden">
      <div v-if="loading" class="text-center py-8">
        <i class="fas fa-spinner fa-spin text-3xl text-gray-400"></i>
      </div>

      <div v-else-if="logs.length === 0" class="text-center py-8 text-gray-500">
        No sync logs found for the selected filters.
      </div>

      <table v-else class="w-full">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Date/Time
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Campaign
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Status
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              SF Record
            </th>
            <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Error
            </th>
            <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200">
          <tr 
            v-for="log in logs" 
            :key="log.id"
            class="hover:bg-gray-50"
          >
            <td class="px-4 py-3 text-sm">
              {{ formatDate(log.created_at) }}
            </td>
            <td class="px-4 py-3 text-sm">
              <a 
                :href="`/campaigns/${log.campaign_id}`"
                target="_blank"
                class="text-blue-600 hover:underline"
              >
                {{ log.campaign?.name || `Campaign #${log.campaign_id}` }}
              </a>
            </td>
            <td class="px-4 py-3 text-sm">
              <span 
                class="inline-block px-2 py-1 text-xs rounded-full"
                :class="getStatusClass(log.status)"
              >
                {{ log.status }}
              </span>
            </td>
            <td class="px-4 py-3 text-sm">
              <div v-if="log.sf_record_id || log.salesforce_id">
                <span class="text-gray-600">{{ log.sf_record_type || 'Lead' }}:</span>
                <a 
                  :href="getSalesforceUrl(log.sf_record_id || log.salesforce_id, log.sf_record_type)"
                  target="_blank"
                  class="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline"
                  title="View in Salesforce"
                >
                  {{ log.sf_record_id || log.salesforce_id }}
                </a>
              </div>
              <span v-else class="text-gray-400">-</span>
            </td>
            <td class="px-4 py-3 text-sm">
              <div v-if="log.error_message" class="text-red-600 text-xs">
                {{ truncate(log.error_message, 50) }}
              </div>
              <span v-else class="text-gray-400">-</span>
            </td>
            <td class="px-4 py-3 text-sm text-center">
              <button 
                @click="viewDetails(log)"
                class="text-blue-600 hover:text-blue-800 mr-2"
                title="View Details"
              >
                <i class="fas fa-eye"></i>
              </button>
              <button 
                v-if="log.status === 'failed'"
                @click="retrySync(log)"
                class="text-orange-600 hover:text-orange-800"
                title="Retry Sync"
              >
                <i class="fas fa-redo"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Load More -->
      <div v-if="hasMorePages" class="load-more px-4 py-3 bg-gray-50 text-center">
        <div class="text-sm text-gray-700 mb-3">
          Showing {{ logs.length }} of {{ pagination.total }} results
        </div>
        <button 
          @click="loadMore"
          :disabled="loadingMore"
          class="btn btn-sm btn-primary"
        >
          <i v-if="loadingMore" class="fas fa-spinner fa-spin mr-2"></i>
          {{ loadingMore ? 'Loading...' : 'Load More' }}
        </button>
      </div>
    </div>

    <!-- Detail Modal -->
    <div 
      v-if="selectedLog"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="selectedLog = null"
    >
      <div class="bg-white rounded-lg p-6 max-w-4xl w-full max-h-screen overflow-y-auto">
        <div class="flex justify-between items-start mb-4">
          <h4 class="text-lg font-semibold">Sync Log Details</h4>
          <button 
            @click="selectedLog = null"
            class="text-gray-500 hover:text-gray-700"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label class="block text-sm font-medium text-gray-700">Log ID</label>
            <p class="text-sm">{{ selectedLog.id }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Timestamp</label>
            <p class="text-sm">{{ formatDate(selectedLog.created_at) }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Campaign</label>
            <p class="text-sm">{{ selectedLog.campaign?.name }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Status</label>
            <p class="text-sm">
              <span 
                class="inline-block px-2 py-1 text-xs rounded-full"
                :class="getStatusClass(selectedLog.status)"
              >
                {{ selectedLog.status }}
              </span>
            </p>
          </div>
        </div>

        <div v-if="selectedLog.sf_record_id" class="mb-4">
          <label class="block text-sm font-medium text-gray-700">Salesforce Record</label>
          <p class="text-sm">
            {{ selectedLog.sf_record_type }}: 
            <span class="font-mono">{{ selectedLog.sf_record_id }}</span>
          </p>
        </div>

        <div v-if="selectedLog.error_message" class="mb-4">
          <label class="block text-sm font-medium text-gray-700">Error Message</label>
          <p class="text-sm text-red-600 bg-red-50 p-2 rounded">
            {{ selectedLog.error_message }}
          </p>
        </div>

        <div v-if="selectedLog.request_data" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Request Data</label>
          <pre class="bg-gray-100 p-3 rounded text-xs overflow-x-auto">{{ JSON.stringify(selectedLog.request_data, null, 2) }}</pre>
        </div>

        <div v-if="selectedLog.response_data" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Response Data</label>
          <pre class="bg-gray-100 p-3 rounded text-xs overflow-x-auto">{{ JSON.stringify(selectedLog.response_data, null, 2) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';

export default {
  name: 'SyncHistory',
  props: {
    configurationId: {
      type: [Number, String],
      default: null,
    },
  },
  emits: ['close'],
  setup(props) {
    const logs = ref([]);
    const loading = ref(true);
    const loadingMore = ref(false);
    const selectedLog = ref(null);
    
    const filters = ref({
      status: '',
      from_date: '',
      to_date: '',
    });

    const pagination = ref({
      current_page: 1,
      last_page: 1,
      from: 0,
      to: 0,
      total: 0,
    });

    const stats = ref({
      total: 0,
      success: 0,
      failed: 0,
      success_rate: 0,
    });
    
    const instanceUrl = ref('');

    const fetchLogs = async (page = 1, append = false) => {
      if (append) {
        loadingMore.value = true;
      } else {
        loading.value = true;
        logs.value = []; // Reset logs for fresh fetch
      }
      
      try {
        const params = {
          page,
          ...filters.value,
        };

        if (props.configurationId) {
          params.configuration_id = props.configurationId;
        }

        const response = await axios.get('/admin/salesforce/logs', { params });
        
        if (append) {
          // Append new logs to existing ones
          logs.value = [...logs.value, ...(response.data.data || [])];
        } else {
          // Replace logs with new ones
          logs.value = response.data.data || [];
        }
        
        pagination.value = {
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          from: response.data.from,
          to: response.data.to,
          total: response.data.total,
        };

        // Fetch stats independently (only on initial load)
        if (!append) {
          fetchStats();
        }
      } catch (error) {
        console.error('Failed to fetch logs:', error);
      } finally {
        loading.value = false;
        loadingMore.value = false;
      }
    };

    const fetchStats = async () => {
      try {
        const params = { ...filters.value };
        
        if (props.configurationId) {
          params.configuration_id = props.configurationId;
        }
        
        const response = await axios.get('/admin/salesforce/logs-stats', { params });
        
        stats.value = {
          total: response.data.total,
          success: response.data.success,
          failed: response.data.failed,
          success_rate: response.data.success_rate,
        };
      } catch (error) {
        console.error('Failed to fetch stats:', error);
        // Fallback to calculating from current page
        const total = pagination.value.total || 0;
        const successCount = logs.value.filter(l => l.status === 'success').length;
        const failedCount = logs.value.filter(l => l.status === 'failed').length;
        
        stats.value = {
          total,
          success: successCount,
          failed: failedCount,
          success_rate: total > 0 ? Math.round((successCount / total) * 100) : 0,
        };
      }
    };

    const clearFilters = () => {
      filters.value = {
        status: '',
        from_date: '',
        to_date: '',
      };
      fetchLogs();
    };

    const loadMore = () => {
      const nextPage = pagination.value.current_page + 1;
      if (nextPage <= pagination.value.last_page) {
        fetchLogs(nextPage, true);
      }
    };

    const hasMorePages = computed(() => {
      return pagination.value.current_page < pagination.value.last_page;
    });

    const viewDetails = (log) => {
      selectedLog.value = log;
    };

    const retrySync = async (log) => {
      if (!confirm('Are you sure you want to retry this sync?')) {
        return;
      }

      try {
        const response = await axios.post(`/admin/salesforce/sync/${log.campaign_id}`);
        if (response.data.success) {
          alert('Sync retry initiated successfully');
          fetchLogs(1); // Start fresh from page 1
        } else {
          alert('Failed to retry sync: ' + response.data.message);
        }
      } catch (error) {
        console.error('Retry sync error:', error);
        alert('Failed to retry sync');
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString();
    };

    const truncate = (text, length) => {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    };

    const getStatusClass = (status) => {
      switch (status) {
        case 'success':
          return 'bg-green-100 text-green-800';
        case 'failed':
          return 'bg-red-100 text-red-800';
        case 'pending':
          return 'bg-yellow-100 text-yellow-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };
    
    const getSalesforceUrl = (recordId, recordType) => {
      if (!recordId) return '#';
      // Default to Salesforce production URL if instance URL not available
      const baseUrl = instanceUrl.value || 'https://side-out.my.salesforce.com';
      // Use the actual record type from the sync log, default to Lead if not specified
      const objectType = recordType || 'Lead';
      return `${baseUrl}/lightning/r/${objectType}/${recordId}/view`;
    };
    
    const fetchInstanceUrl = async () => {
      try {
        const response = await axios.get('/admin/salesforce/monitoring/stats');
        if (response.data && response.data.instance_url) {
          instanceUrl.value = response.data.instance_url;
        }
      } catch (error) {
        console.error('Failed to fetch instance URL:', error);
      }
    };

    onMounted(() => {
      fetchLogs();
      fetchStats();
      fetchInstanceUrl();
    });

    return {
      logs,
      loading,
      loadingMore,
      selectedLog,
      filters,
      pagination,
      stats,
      hasMorePages,
      fetchLogs,
      fetchStats,
      clearFilters,
      loadMore,
      viewDetails,
      retrySync,
      formatDate,
      truncate,
      getStatusClass,
      getSalesforceUrl,
    };
  },
};
</script>

<style scoped>
.form-select,
.form-input {
  @apply border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500;
}

.btn {
  @apply px-4 py-2 rounded font-medium transition-colors;
}

.btn-secondary {
  @apply bg-gray-500 text-white hover:bg-gray-600;
}

.btn-primary {
  @apply bg-blue-500 text-white hover:bg-blue-600;
}

.btn-sm {
  @apply px-2 py-1 text-sm;
}

.btn:disabled {
  @apply opacity-50 cursor-not-allowed;
}
</style>